import { useNavigate } from "react-router";
import { LoadingSpinner } from "@kubdee/ui/custom/feedback/loading-spinner";
import { useState, useEffect, useCallback } from "react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
  CommandEmpty,
  CommandSeparator,
  CommandShortcut,
} from "@kubdee/ui/shadcn/command";
import {
  SearchIcon,
  ClockIcon,
  ArrowRightIcon,
  ZapIcon,
  Trash2Icon,
  type LucideIcon,
} from "lucide-react";
import {
  useGlobalSearch,
  type SearchResultItem,
} from "@kubdee/sdk/hooks/use-global-search";
import {
  navigationItems,
  quickActions,
  filterNavigationItems,
  filterQuickActions,
} from "@kubdee/ui/lib/navigation-registry";
import { getModuleIcon } from "@kubdee/ui/lib/icon-registry";

// ---------------------------------------------------------------------------
// Icon resolver — maps string names to lucide components
// ---------------------------------------------------------------------------

// We reuse icon-registry for module icons. For navigation/action icons we
// import a small set directly.
import {
  Car,
  CalendarDays,
  MapPin,
  FileText,
  CreditCard,
  Users,
  Building,
  Calculator,
  BookOpen,
  Wrench,
  UserPlus,
  Handshake,
  Contact,
  Building2,
  Settings,
  Shield,
  Plus,
} from "lucide-react";

const NAV_ICONS: Record<string, LucideIcon> = {
  Car,
  CalendarDays,
  MapPin,
  FileText,
  CreditCard,
  Users,
  Building,
  Calculator,
  BookOpen,
  Wrench,
  UserPlus,
  Handshake,
  Contact,
  Building2,
  Settings,
  Shield,
  Plus,
};

function resolveIcon(name: string): LucideIcon {
  return NAV_ICONS[name] ?? getModuleIcon(name);
}

// ---------------------------------------------------------------------------
// CommandPalette
// ---------------------------------------------------------------------------

export function CommandPalette() {
  const navigate = useNavigate();
  const {
    query,
    setQuery,
    results,
    recent,
    isLoading,
    logSearch,
    clearHistory,
    reset,
  } = useGlobalSearch();

  // ---- Open / close state ----
  const [open, setOpen] = useState(false);

  // ---- Keyboard shortcut: Cmd+K / Ctrl+K ----
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Reset query when closing
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  // ---- Handlers ----
  const navigateTo = useCallback(
    (path: string) => {
      setOpen(false);
      navigate(path);
    },
    [navigate]
  );

  const handleSelectResult = useCallback(
    (item: SearchResultItem) => {
      logSearch(item);
      setOpen(false);
      navigate(item.link);
    },
    [logSearch, navigate]
  );

  const handleSelectRecent = useCallback(
    (item: {
      resultDocumentId?: string;
      resultModule?: string;
      resultDoctype?: string;
      resultTitle?: string;
      query: string;
    }) => {
      if (item.resultDocumentId && item.resultModule && item.resultDoctype) {
        setOpen(false);
        navigate(
          `/${item.resultModule}/${item.resultDoctype}/${item.resultDocumentId}`
        );
      } else {
        // Re-search with saved query
        setQuery(item.query);
      }
    },
    [navigate, setQuery]
  );

  // ---- Determine mode ----
  const trimmedQuery = query.trim();
  const isNavigateMode = trimmedQuery.startsWith("/");
  const isActionMode = trimmedQuery.startsWith(">");
  const isSearchMode =
    !isNavigateMode && !isActionMode && trimmedQuery.length >= 2;
  const isEmptyMode = trimmedQuery.length === 0;

  // ---- Filtered navigation / actions ----
  const navQuery = isNavigateMode ? trimmedQuery.slice(1) : "";
  const filteredNav = isNavigateMode
    ? filterNavigationItems(navigationItems, navQuery)
    : [];

  const actionQuery = isActionMode ? trimmedQuery.slice(1) : "";
  const filteredActions = isActionMode
    ? filterQuickActions(quickActions, actionQuery)
    : [];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="ค้นหา... (⌘K)"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {/* Loading indicator */}
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <LoadingSpinner size="md" className="text-muted-foreground" />
            <span className="ml-2 text-xs text-muted-foreground">
              กำลังค้นหา...
            </span>
          </div>
        )}

        {/* =================== Empty mode: recent + nav + actions =================== */}
        {isEmptyMode && (
          <>
            {/* Recent searches */}
            {recent.length > 0 && (
              <CommandGroup heading="ล่าสุด">
                {recent.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={`recent-${item.id}`}
                    onSelect={() => handleSelectRecent(item)}
                  >
                    <ClockIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate">
                      {item.resultTitle || item.query}
                    </span>
                    {item.resultDoctype && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {item.resultDoctype}
                      </span>
                    )}
                  </CommandItem>
                ))}
                <CommandItem
                  value="clear-history"
                  onSelect={() => clearHistory()}
                  className="text-muted-foreground"
                >
                  <Trash2Icon className="h-4 w-4 shrink-0" />
                  <span className="text-xs">ล้างประวัติ</span>
                </CommandItem>
              </CommandGroup>
            )}

            <CommandSeparator />

            {/* Navigate shortcuts */}
            <CommandGroup heading="ไปที่...">
              {navigationItems.slice(0, 6).map((item) => {
                const Icon = resolveIcon(item.icon);
                return (
                  <CommandItem
                    key={item.path}
                    value={`nav-${item.path}`}
                    onSelect={() => navigateTo(item.path)}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span>{item.label}</span>
                    <CommandShortcut>{item.path}</CommandShortcut>
                  </CommandItem>
                );
              })}
            </CommandGroup>

            <CommandSeparator />

            {/* Quick actions */}
            <CommandGroup heading="Actions">
              {quickActions.slice(0, 4).map((action) => {
                const Icon = resolveIcon(action.icon);
                return (
                  <CommandItem
                    key={action.path}
                    value={`action-${action.path}`}
                    onSelect={() => navigateTo(action.path)}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span>{action.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}

        {/* =================== Navigate mode (/ prefix) =================== */}
        {isNavigateMode && (
          <CommandGroup heading="ไปที่...">
            {filteredNav.length === 0 && (
              <CommandEmpty>ไม่พบหน้าที่ตรงกัน</CommandEmpty>
            )}
            {filteredNav.map((item) => {
              const Icon = resolveIcon(item.icon);
              return (
                <CommandItem
                  key={item.path}
                  value={`nav-${item.path}`}
                  onSelect={() => navigateTo(item.path)}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span>{item.label}</span>
                  <CommandShortcut>{item.path}</CommandShortcut>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* =================== Action mode (> prefix) =================== */}
        {isActionMode && (
          <CommandGroup heading="Actions">
            {filteredActions.length === 0 && (
              <CommandEmpty>ไม่พบ action ที่ตรงกัน</CommandEmpty>
            )}
            {filteredActions.map((action) => {
              const Icon = resolveIcon(action.icon);
              return (
                <CommandItem
                  key={action.path}
                  value={`action-${action.path}`}
                  onSelect={() => navigateTo(action.path)}
                >
                  <ZapIcon className="mr-1 h-3 w-3 shrink-0 text-amber-500" />
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span>{action.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* =================== Search mode (API results) =================== */}
        {isSearchMode && !isLoading && results && (
          <>
            {results.results.map((group) => {
              const GroupIcon = getModuleIcon(group.icon);
              return (
                <CommandGroup
                  key={`${group.module}-${group.doctype}`}
                  heading={group.group}
                >
                  {group.items.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={`result-${item.id}`}
                      onSelect={() => handleSelectResult(item)}
                    >
                      <GroupIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="flex flex-1 items-center gap-2 truncate">
                        <span className="truncate">{item.title}</span>
                        {item.subtitle && (
                          <span className="truncate text-xs text-muted-foreground">
                            {item.subtitle}
                          </span>
                        )}
                      </div>
                      <ArrowRightIcon className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-data-[selected=true]:opacity-100" />
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
            {results.results.length === 0 && (
              <CommandEmpty>ไม่พบผลลัพธ์</CommandEmpty>
            )}
          </>
        )}

        {/* Search mode but not enough chars */}
        {!isNavigateMode &&
          !isActionMode &&
          !isEmptyMode &&
          trimmedQuery.length < 2 && (
            <div className="py-6 text-center text-xs text-muted-foreground">
              พิมพ์อย่างน้อย 2 ตัวอักษรเพื่อค้นหา
            </div>
          )}

        {/* Footer hint */}
        <div className="flex items-center justify-between border-t px-3 py-2 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <span>
              <kbd className="rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
                /
              </kbd>{" "}
              ไปที่หน้า
            </span>
            <span>
              <kbd className="rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
                &gt;
              </kbd>{" "}
              actions
            </span>
          </div>
          <div className="flex items-center gap-1">
            <kbd className="rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
              ↑↓
            </kbd>
            <span>เลื่อน</span>
            <kbd className="ml-1 rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
              Enter
            </kbd>
            <span>เลือก</span>
            <kbd className="ml-1 rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
              Esc
            </kbd>
            <span>ปิด</span>
          </div>
        </div>
      </CommandList>
    </CommandDialog>
  );
}
